'use client';

import React, { useState, useRef, useEffect, ReactNode, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from '@/components/icons';
import { Button } from '@/components/atoms';

export interface DropdownItem {
  id: string;
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
  onAsyncClick?: () => Promise<void>;
}

export interface DropdownProps {
  trigger: string | React.ReactElement;
  items: DropdownItem[];
  header?: ReactNode;
  footer?: ReactNode;
  align?: 'left' | 'right';
  width?: string;
  showChevron?: boolean;
}

export const Dropdown = ({
  trigger: _trigger,
  items,
  header,
  footer,
  align = 'left',
  width = 'w-52',
  showChevron = true,
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is inside any dropdown portal
      const dropdownPortals = document.querySelectorAll('[data-dropdown-portal]');
      const isInsidePortal = Array.from(dropdownPortals).some(portal => 
        portal.contains(event.target as Node)
      );
      
      if (!isInsidePortal && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const trigger = useMemo(() => {
    if (_trigger && typeof _trigger !== 'string') {
      return React.cloneElement(_trigger as any, {
        onClick: () => {
          if (dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            setPosition({
              top: rect.bottom + 8,
              left: align === 'right' ? rect.right - 200 : rect.left
            });
          }
          setIsOpen(prev => !prev);
        },
      })
    }
    return (
      <Button variant="primary" onClick={() => setIsOpen(prev => !prev)}>
        {_trigger}
        {showChevron && <ChevronDownIcon />}
      </Button>
    )

  }, [_trigger, showChevron, align]);

  const alignClass = align === 'right' ? 'right-0' : 'left-0';

  return (
    <>
      <div className="relative w-max" ref={dropdownRef}>
        {trigger}
      </div>

      {isOpen && createPortal(
        <div
          data-dropdown-portal="true"
          className={`absolute bg-white rounded-lg shadow-lg p-4 ${width} animate-fadeIn`}
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 99999
          }}
        >
          {header && <div className="mb-4 pb-4 border-b border-gray-200">{header}</div>}

          <div className="space-y-3 text-sm font-roboto">
            {items.map((item) => (
              <button
                key={item.id}
                onClick={async () => {
                  if (item.onAsyncClick) {
                    await item.onAsyncClick();
                  } else {
                    item.onClick?.();
                  }
                  setIsOpen(false);
                }}
                className="w-full flex cursor-pointer items-center gap-3 py-1 rounded-lg transition-colors text-left bg-white text-gray-900 hover:text-secondary"
              >
                {item.icon && (
                  <div className="w-5 h-5 text-gray-700 shrink-0">
                    {item.icon}
                  </div>
                )}
                <span className="font-normal">
                  {item.label}
                </span>
              </button>
            ))}
          </div>

          {footer && <div className="mt-4">{footer}</div>}
        </div>,
        document.body
      )}
    </>
  );
};
